import { Link, useNavigate } from "@tanstack/react-router";
import { FavoriteButton } from "components/elements/FavoriteButton";
import { PreviewMediaCard } from "components/elements/PreviewMediaCard";
import { formatISODateRelative } from "lib/formatISODate";
import { renderSanitizedHTML } from "lib/renderSanitizedHTML";
import { ResourcesPostPreview } from "@compass/shared/types";

export const BlogPostPreview = ({ post }: { post: ResourcesPostPreview }) => {
    const navigate = useNavigate();
    return (
        <div className="relative flex flex-col overflow-hidden rounded-2xl border border-[#c8c8c8] bg-[#fafffe] p-2 no-underline">
            <div className="relative">
                {/* Media card handles its own navigation: image variants wrap
                 * in a Link via `linkSlug`; interactive variants (embeds /
                 * video / podcast) intentionally don't navigate so users can
                 * play in place. */}
                <PreviewMediaCard
                    preview={post.preview}
                    title={post.title}
                    variant="compact"
                    linkSlug={post.slug}
                />
                <div className="absolute top-2 right-2 z-10">
                    <FavoriteButton
                        target={{ type: "blogPost", id: post.id }}
                        size={14}
                        className="bg-white/90 hover:bg-white"
                    />
                </div>
            </div>

            <Link
                to="/p/$slug"
                params={{ slug: post.slug }}
                className="flex w-full flex-col gap-1 p-3 no-underline"
            >
                <span className="font-sans text-xs font-normal text-gray-500">
                    {formatISODateRelative(post.publishedAt)}
                </span>
                <div className="line-clamp-2 break-words font-sans text-[14px] font-semibold leading-[125%] text-[#424242]">
                    <div {...renderSanitizedHTML(post.title)} />
                </div>
                {post.excerpt && (
                    <div className="line-clamp-3 break-words font-sans text-sm font-normal text-[#424242] [&_*]:!inline">
                        <div {...renderSanitizedHTML(post.excerpt)} />
                    </div>
                )}
            </Link>
            {post.author?.name && (
                <div className="-mt-1 truncate px-3 pb-3 font-sans text-xs font-semibold text-gray-500">
                    {post.author.id != null ? (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                navigate({
                                    to: "/author/$id",
                                    params: { id: String(post.author.id) },
                                });
                            }}
                            className="cursor-pointer border-none bg-transparent p-0 underline"
                        >
                            {post.author.name}
                        </button>
                    ) : (
                        post.author.name
                    )}
                </div>
            )}
        </div>
    );
};
